import { ChatBot, NextPage, NextPageHead, PageView, VWOExperimentPush } from '@aspendental/shared-components-web';
import { PageThemeContextProvider } from '@aspendental/shared-components-web/lib/context/PageThemeProvider.ctx';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { fetchAppData } from './utils/app-data-fetcher';
import getConfigByBrand from './utils/get-config-by-brand';
import { getDeviceTypeFromHeaders } from './utils/get-device-type-from-headers';
import getHostFromHeaders from './utils/get-host-from-headers';
import getThemeByDomainName from './utils/get-theme-by-domain-name';
import { getUserCoordinates } from './utils/get-user-coordinates';
import { processPageContents } from './utils/page-contents-processor';

import { CACHE_CONTROL_NO_CACHE, CACHE_CONTROL_SHORT_TTL } from '@src/constants';
import { LandingPageByAppNameWithPathDocument, PageFragment } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import { LAYOUT_FEATURE_FLAGS } from '@src/lib/vwo-fme/layout-feature-flags';
import { resolveFeatureFlags } from '@src/lib/vwo-fme/resolve-feature-flags';
import { resolveServerSideExperiments } from '@src/lib/vwo-fme/resolve-server-side-experiments';
import choosePreview from '@src/pages/utils/choose-preview';
import DetermineIndexing from '@src/pages/utils/determine-indexing';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';

const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
	const pageData = useContentfulLiveUpdates(props.page);
	const { url: logoUrl } = props.appData?.logo ?? {};
	const {
		path,
		title,
		indexed,
		description,
		canonical,
		breadcrumbsCollection,
		disableChatBot = false,
		analyticsContext,
		ogImage,
	} = pageData;
	const pageTheme = props.page?.pageTheme || 'Primary';
	const { siteURL, features, featureFlags, logo } = props.config as BrandThemeConfig;
	const { preview, pageReferrer, domain } = props;
	return (
		<PageThemeContextProvider pageTheme={pageTheme}>
			<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Marketing" />
			<NextPageHead
				path={path}
				title={title}
				indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)}
				description={description}
				canonical={canonical}
				originUrl={siteURL}
				breadcrumbs={breadcrumbsCollection?.items}
				breadcrumbTitle={pageData.breadcrumbTitle}
				ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark}
				contentsCollection={pageData.contentsCollection}
			/>
			<VWOExperimentPush decisions={props.vwoDecisions ?? []} />
			<NextPage pageData={{ ...pageData }} />
			{!disableChatBot && (
				<ChatBot
					url={features.chat.chatBotURL}
					isIconButton={features.chat.isIconButton}
					logoUrl={logoUrl}
					featureList={featureFlags.featureList}
				/>
			)}
		</PageThemeContextProvider>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ query, locale, req, res, preview = false }) => {
	const domain = getHostFromHeaders(req?.headers);
	const deviceType = getDeviceTypeFromHeaders(req.headers);
	const theme = getThemeByDomainName(domain);
	const headerTagTheme = Array.isArray(req.headers['x-tag-theme'])
		? (req.headers['x-tag-theme'] as TagThemeType[])[0]
		: (req.headers['x-tag-theme'] as TagThemeType);
	// Priority: HTTP header x-tag-theme > domain > process.env.TAG_BRAND_THEME
	const tagTheme: TagThemeType = headerTagTheme || theme || process.env.TAG_BRAND_THEME || 'aspendental';
	const logger = getLogger('home', tagTheme);
	const pageReferrer = req.headers.referer || '';
	const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';

	try {
		const brandConfig = getConfigByBrand(tagTheme);
		preview = choosePreview(domain, preview);

		const gqlClient = getClient(preview);
		if (gqlClient === null) {
			logger.error('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
			throw new Error('gqlClient is null');
		}
		const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';

		const appData = await fetchAppData(gqlClient, appName, preview, logger);
		if (!appData) {
			return {
				notFound: true,
			};
		}

		const pageData = await gqlClient
			.query(LandingPageByAppNameWithPathDocument, { appName, path: '/', preview })
			.toPromise();

		if (pageData.error) {
			logger.error(`CONTENTFUL_ERROR_HOME_PAGE: ${pageData.error}`);
		}

		const page = pageData.data?.pageCollection?.items?.[0] as PageFragment | undefined;

		if (!page) {
			if (pageData.error) {
				// Contentful errored with no usable data — mark it uncacheable so the
				// CDN can't cache this blip and amplify it into a sustained probe failure.
				// Note: the error may be transient (network/429/5xx) or permanent (schema
				// mismatch, query complexity). We log as NO_DATA rather than TRANSIENT to
				// avoid misleading alert labels, and include the error for debugging.
				res.setHeader('Cache-Control', 'private, no-store');
				logger.error(`CONTENTFUL_ERROR_NO_DATA_HOME_PAGE: ${pageData.error}`);
			} else {
				logger.error(`COULD_NOT_FIND_THE_HOME_PAGE`);
			}
			return {
				notFound: true,
			};
		}

		// Process page contents: fetch by ID, enrich with entry links
		const enrichedPageContents = await processPageContents(
			gqlClient,
			preview,
			logger,
			page.contentsCollection?.items
		);
		// Resolve VWO FME feature flags for Server-Side ExperimentContainers
		const { decisions: experimentDecisions } = await resolveServerSideExperiments(
			enrichedPageContents,
			brandConfig,
			req,
			res,
			query,
			logger
		);

		// Resolve standalone VWO FME flags (layout-level header swap, etc.)
		const { flags: serverFeatureFlags, decisions: flagDecisions } = await resolveFeatureFlags(
			LAYOUT_FEATURE_FLAGS,
			brandConfig,
			req,
			res,
			query,
			logger
		);

		const vwoDecisions = [...experimentDecisions, ...flagDecisions];

		if (preview) {
			res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
		} else if (!res.getHeader('Cache-Control')) {
			// Skip if an earlier resolver already pinned the response to a
			// non-shareable Cache-Control (e.g. visitor-id mint sets
			// "private, no-store" to prevent CDN cookie poisoning).
			res.setHeader('Cache-Control', CACHE_CONTROL_SHORT_TTL);
		}

		const { coordinates, hasIpBasedCoordinates } = getUserCoordinates(req, logger);

		const transformedServices = getConfigServices(brandConfig);

		return {
			props: {
				...(await getServerSideTranslations(locale)),
				preview,
				env: {
					ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
					CC_DENTRINO_CLIENT_ID: process.env.CC_DENTRINO_CLIENT_ID ?? '',
					CC_DENTRINO_CLIENT_SECRET: process.env.CC_DENTRINO_CLIENT_SECRET ?? '',
					DENTRINO_CLIENT_ID: process.env.DENTRINO_CLIENT_ID,
					DENTRINO_CLIENT_SECRET: process.env.DENTRINO_CLIENT_SECRET ?? '',
				},
				config: {
					...brandConfig,
					...(appData.featureFlags ? { featureFlags: appData.featureFlags } : {}),
					services: transformedServices,
					schemaUrl: `${brandConfig.siteURL}${req.url}`,
				},
				page: {
					...page,
					contentsCollection: { items: enrichedPageContents },
				},
				appData,
				user: {
					coordinates,
					hasIpBasedCoordinates,
				},
				footerData: appData?.footer,
				deviceType,
				pageReferrer,
				stateCodeFromHeaders,
				domain: domain ?? '',
				vwoDecisions,
				serverFeatureFlags,
			},
		};
	} catch (e) {
		// Override any previously-set cacheable header (e.g. CACHE_CONTROL_SHORT_TTL
		// set after a successful page fetch) so the CDN doesn't cache the error 404.
		res.setHeader('Cache-Control', 'private, no-store');
		logger.error(`HOME_PAGE_LOAD_FAILURE: ${e}`);
		return {
			notFound: true,
		};
	}
};

export default Home;
